// The week view (B6, Cal-17): one row per person, one cell per day — their
// hours or Day off, and how much work that day holds. A day's header carries
// its session total and, when any, how many bookings still need a therapist.
// Clicking a cell opens that day; the detail lives on the day board.

import {
  buildCalendarDay,
  type CalendarFilters,
  type CalendarSource,
  inHub,
  initialsOf,
  isToday,
  type MinuteSpan,
} from "./calendar-board";
import { addCalendarDays } from "./calendar-model";

/** Monday of the Manila week holding `day`. */
export function weekStartOf(day: string): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addCalendarDays(day, -((weekday + 6) % 7));
}

/** The seven days, Monday first, of the week holding `day`. */
export function weekDaysOf(day: string): string[] {
  const start = weekStartOf(day);
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(start, index));
}

export interface WeekCell {
  day: string;
  /** No published entry for this person that day. */
  unscheduled: boolean;
  dayOff: boolean;
  shift: MinuteSpan | null;
  /** Sessions for a therapist, legs for a rider. */
  count: number;
  isToday: boolean;
}

export interface WeekRow {
  staffId: string;
  name: string;
  initials: string;
  role: "therapist" | "rider";
  hubId: string;
  hubName: string;
  cells: WeekCell[];
}

export interface WeekDayHead {
  day: string;
  sessions: number;
  unassigned: number;
  isToday: boolean;
}

export interface CalendarWeek {
  heads: WeekDayHead[];
  rows: WeekRow[];
}

export function buildCalendarWeek(
  source: CalendarSource,
  days: readonly string[],
  nowMs: number,
  filters: CalendarFilters,
): CalendarWeek {
  const boards = days.map((day) => buildCalendarDay(source, day, nowMs));
  const query = filters.search.trim().toLowerCase();
  const hubOrder = new Map(source.hubs.map((hub, index) => [hub.id, index]));

  const people = new Map<string, Omit<WeekRow, "cells">>();
  for (const board of boards) {
    for (const row of board.rows) {
      if (people.has(row.staffId)) continue;
      people.set(row.staffId, {
        staffId: row.staffId,
        name: row.name,
        initials: initialsOf(row.name),
        role: row.role,
        hubId: row.hubId,
        hubName: row.hubName,
      });
    }
  }

  const rows = [...people.values()]
    .filter(
      (person) =>
        inHub(filters, person.hubId) &&
        (filters.who === "all" || (filters.who === "riders") === (person.role === "rider")) &&
        (query === "" || person.name.toLowerCase().includes(query)),
    )
    .sort(
      (a, b) =>
        (hubOrder.get(a.hubId) ?? 99) - (hubOrder.get(b.hubId) ?? 99) ||
        (a.role === b.role ? a.name.localeCompare(b.name) : a.role === "therapist" ? -1 : 1),
    )
    .map(
      (person): WeekRow => ({
        ...person,
        cells: boards.map((board) => {
          const row = board.rows.find((entry) => entry.staffId === person.staffId);
          return {
            day: board.day,
            unscheduled: !row,
            dayOff: row?.dayOff ?? false,
            shift: row?.shift ?? null,
            count: row?.blocks.length ?? 0,
            isToday: isToday(board),
          };
        }),
      }),
    );

  const heads = boards.map((board) => ({
    day: board.day,
    sessions: board.sessions.filter(
      (session) => session.phase !== "unassigned" && inHub(filters, session.hubId),
    ).length,
    unassigned: board.unassigned.filter((session) => inHub(filters, session.hubId)).length,
    isToday: isToday(board),
  }));

  return { heads, rows };
}
